import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import type { MetaAdsPage } from '@/features/integrations/types';
import { Facebook, Instagram } from 'lucide-react';
import { useEffect, useId } from 'react';
import { useController, useFormContext } from 'react-hook-form';
import { type AdWizardFormData, adWizardLabels } from '../../-schema';

export function AdPlacementFields({ page }: { page?: MetaAdsPage }) {
  const id = useId();
  const { control, resetField, getFieldState, getValues } =
    useFormContext<AdWizardFormData>();
  const { field, fieldState } = useController({ control, name: 'adPlacement' });
  const hasInstagram = Boolean(page?.linkedInstagramAccountId);
  const pageId = page?.id;

  useEffect(() => {
    if (!pageId) return;
    const value = getValues('adPlacement');
    const needsInstagram = value === 'both' || value === 'instagram';
    if (
      !getFieldState('adPlacement').isDirty ||
      (!hasInstagram && needsInstagram)
    ) {
      resetField('adPlacement', {
        defaultValue: hasInstagram ? 'both' : 'facebook',
      });
    }
  }, [pageId, hasInstagram, getValues, getFieldState, resetField]);

  const facebook = field.value === 'facebook' || field.value === 'both';
  const instagram = field.value === 'instagram' || field.value === 'both';
  const select = (platform: 'facebook' | 'instagram', checked: boolean) => {
    const nextFacebook = platform === 'facebook' ? checked : facebook;
    const nextInstagram = platform === 'instagram' ? checked : instagram;
    field.onChange(
      nextFacebook && nextInstagram
        ? 'both'
        : nextFacebook
          ? 'facebook'
          : nextInstagram
            ? 'instagram'
            : ''
    );
  };

  return (
    <fieldset
      className="space-y-3"
      aria-describedby={fieldState.error ? `${id}-error` : undefined}
    >
      <legend className="text-sm font-medium">
        {adWizardLabels.adPlacement}
      </legend>
      <div className="flex flex-wrap gap-6">
        <div className="flex items-center gap-2">
          <Checkbox
            id={`${id}-facebook`}
            checked={facebook}
            disabled={!page}
            onCheckedChange={(checked) => select('facebook', checked === true)}
            onBlur={field.onBlur}
          />
          <Label htmlFor={`${id}-facebook`} className="flex items-center gap-2">
            <Facebook className="size-4" />
            Facebook
          </Label>
        </div>
        <div className="flex items-center gap-2">
          <Checkbox
            id={`${id}-instagram`}
            checked={instagram}
            disabled={!hasInstagram}
            onCheckedChange={(checked) => select('instagram', checked === true)}
            onBlur={field.onBlur}
          />
          <Label
            htmlFor={`${id}-instagram`}
            className="flex items-center gap-2"
          >
            <Instagram className="size-4" />
            Instagram
          </Label>
        </div>
      </div>
      {page && !hasInstagram ? (
        <p className="text-sm text-muted-foreground">
          Link Instagram to this Facebook Page to use Instagram placements.
        </p>
      ) : null}
      {fieldState.error ? (
        <p id={`${id}-error`} className="text-sm text-destructive">
          {fieldState.error.message}
        </p>
      ) : null}
    </fieldset>
  );
}
